import React, { useState } from 'react';

const buildFormData = (artist) => ({
  name: artist?.name || '',
  slug: artist?.slug || '',
  email: artist?.email || '',
  mvola_number: artist?.mvola_number || '',
  bio: artist?.bio || '',
  website: artist?.website || '',
  instagram: artist?.instagram || '',
  facebook: artist?.facebook || '',
  youtube: artist?.youtube || '',
  banner_url: artist?.banner_url || ''
});

const URL_FIELDS = ['website', 'instagram', 'facebook', 'youtube'];

const validateUrl = (value) => {
  if (!value || value.trim() === '') return '';
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'L\'URL doit commencer par http:// ou https://';
    }
    return '';
  } catch {
    return 'URL invalide (ex: https://example.com)';
  }
};

const ProfileSection = ({
  artist,
  onSave,
  telegramCode,
  telegramCodeExpiresAt,
  telegramCodeLoading,
  telegramCodeError,
  telegramCodeCopied,
  showTelegramModal,
  onShowTelegramModal,
  onGenerateTelegramCode,
  onCopyTelegramCode,
  onTestTelegram,
  formatDateTime
}) => {
  const [formData, setFormData] = useState(() => buildFormData(artist));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(() => (artist?.bio || '').length);
  const [urlErrors, setUrlErrors] = useState({ website: '', instagram: '', facebook: '', youtube: '' });
  const maxBioLength = 500;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'bio') {
      setCharCount(value.length);
    }
    if (URL_FIELDS.includes(field)) {
      setUrlErrors((prev) => ({ ...prev, [field]: validateUrl(value) }));
    }
    setSaved(false);
    setError('');
  };

  const handleUrlBlur = (field) => {
    setUrlErrors((prev) => ({ ...prev, [field]: validateUrl(formData[field]) }));
  };

  const hasUrlErrors = Object.values(urlErrors).some((msg) => msg !== '');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all URL fields before submission
    const newErrors = {};
    URL_FIELDS.forEach((field) => {
      newErrors[field] = validateUrl(formData[field]);
    });
    setUrlErrors(newErrors);

    if (Object.values(newErrors).some((msg) => msg !== '')) {
      setError('Veuillez corriger les URL invalides avant de sauvegarder.');
      return;
    }

    setSaving(true);

    try {
      const savedSuccessfully = await onSave(formData);
      if (!savedSuccessfully) {
        throw new Error('Impossible de sauvegarder le profil.');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      setError(error.message || 'Impossible de sauvegarder le profil.');
    } finally {
      setSaving(false);
    }
  };

  const bioStatus = charCount > maxBioLength * 0.9 ? 'error'
    : charCount > maxBioLength * 0.8 ? 'warning' : 'normal';

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 className="settings-section-title">Informations du profil</h2>
            <p className="settings-section-description">
              Personnalisez votre profil public et vos informations de contact
            </p>
          </div>
          {saving && (
            <span className="save-indicator saving">
              <i className="fa-solid fa-circle-notch fa-spin"></i>
              Sauvegarde...
            </span>
          )}
          {saved && (
            <span className="save-indicator saved">
              <i className="fa-solid fa-check"></i>
              Enregistré
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="settings-alert error">
          <i className="fa-solid fa-triangle-exclamation"></i>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="settings-form-grid">
          <div className="settings-form-field">
            <label className="settings-form-label">Nom d'artiste</label>
            <input
              type="text"
              className="settings-form-input"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Votre nom d'artiste"
              required
            />
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">
              Nom d'utilisateur public
              <span className="optional">(optionnel)</span>
            </label>
            <input
              type="text"
              className="settings-form-input"
              value={formData.slug}
              onChange={(e) => handleChange('slug', e.target.value.replace(/\s+/g, '').toLowerCase())}
              placeholder="nom_utilisateur"
            />
            <p className="settings-form-hint">
              Utilisé pour votre URL de profil: /artist/{formData.slug || 'username'}
            </p>
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">Email de contact</label>
            <input
              type="email"
              className="settings-form-input"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@exemple.com"
            />
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">
              Numéro MVola
              <span className="optional">(optionnel)</span>
            </label>
            <input
              type="tel"
              className="settings-form-input"
              value={formData.mvola_number}
              onChange={(e) => handleChange('mvola_number', e.target.value)}
              placeholder="+261 34 00 000 00"
            />
          </div>
        </div>

        <div className="settings-form-field full-width">
          <label className="settings-form-label">
            Biographie
            <span className="optional">({maxBioLength} caractères max)</span>
          </label>
          <textarea
            className="settings-form-textarea"
            value={formData.bio}
            onChange={(e) => handleChange('bio', e.target.value)}
            placeholder="Parlez-nous de vous, de votre style musical, de vos inspirations..."
            maxLength={maxBioLength}
            rows={5}
          />
          <p className={`char-counter ${bioStatus}`}>
            {charCount}/{maxBioLength}
          </p>
        </div>

        <div className="settings-form-field full-width">
          <label className="settings-form-label">
            Bannière du profil
            <span className="optional">(optionnel)</span>
          </label>
          <input
            type="url"
            className="settings-form-input"
            value={formData.banner_url}
            onChange={(e) => handleChange('banner_url', e.target.value)}
            placeholder="https://..."
          />
          <p className="settings-form-hint">
            URL d’image utilisée comme fond du profil public.
          </p>
        </div>

        <div className="settings-section-header" style={{ marginTop: '1.5rem' }}>
          <h3 className="settings-section-title" style={{ fontSize: '1rem' }}>
            Liens sociaux
          </h3>
          <p className="settings-section-description">
            Connectez vos réseaux sociaux pour plus de visibilité
          </p>
        </div>

        <div className="settings-form-grid">
          <div className="settings-form-field">
            <label className="settings-form-label">
              <i className="fa-brands fa-instagram" style={{ color: '#E4405F' }}></i>
              Instagram
            </label>
            <input
              type="url"
              className="settings-form-input"
              value={formData.instagram}
              onChange={(e) => handleChange('instagram', e.target.value)}
              onBlur={() => handleUrlBlur('instagram')}
              placeholder="https://instagram.com/votre_compte"
            />
            {urlErrors.instagram && <p className="settings-form-error">{urlErrors.instagram}</p>}
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">
              <i className="fa-brands fa-facebook" style={{ color: '#1877F2' }}></i>
              Facebook
            </label>
            <input
              type="url"
              className="settings-form-input"
              value={formData.facebook}
              onChange={(e) => handleChange('facebook', e.target.value)}
              onBlur={() => handleUrlBlur('facebook')}
              placeholder="https://facebook.com/votrepage"
            />
            {urlErrors.facebook && <p className="settings-form-error">{urlErrors.facebook}</p>}
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">
              <i className="fa-brands fa-youtube" style={{ color: '#FF0000' }}></i>
              YouTube
            </label>
            <input
              type="url"
              className="settings-form-input"
              value={formData.youtube}
              onChange={(e) => handleChange('youtube', e.target.value)}
              onBlur={() => handleUrlBlur('youtube')}
              placeholder="https://youtube.com/@votrechaine"
            />
            {urlErrors.youtube && <p className="settings-form-error">{urlErrors.youtube}</p>}
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">
              <i className="fa-solid fa-globe" style={{ color: 'var(--navy)' }}></i>
              Site web
            </label>
            <input
              type="url"
              className="settings-form-input"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              onBlur={() => handleUrlBlur('website')}
              placeholder="https://votresite.com"
            />
            {urlErrors.website && <p className="settings-form-error">{urlErrors.website}</p>}
          </div>

          {/* Telegram Integration Field — masqué pour les Free sans config */}
          {(artist?.subscription_tier === 'premium' || artist?.telegram_id) && (
            <div className="settings-form-field">
              <label className="settings-form-label">
                <i className="fa-brands fa-telegram" style={{ color: '#0088cc' }}></i>
                Telegram Bot
              </label>
              <div className="telegram-field-content" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                {artist?.telegram_id ? (
                  <>
                    <span className="integration-status connected" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, color: '#28a745' }}>
                      <i className="fa-solid fa-circle-check"></i>
                      Déjà configuré (ID: {artist.telegram_id})
                    </span>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={onTestTelegram}
                      style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
                    >
                      <i className="fa-solid fa-paper-plane" style={{ marginRight: '5px' }}></i>
                      Tester
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={async () => {
                        onShowTelegramModal(true);
                        if (!telegramCode) {
                          await onGenerateTelegramCode();
                        }
                      }}
                      style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
                    >
                      Reconfigurer
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={async () => {
                      onShowTelegramModal(true);
                      if (!telegramCode) {
                        await onGenerateTelegramCode();
                      }
                    }}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.8125rem' }}
                  >
                    <i className="fa-brands fa-telegram" style={{ marginRight: '5px' }}></i>
                    Connecter Telegram
                  </button>
                )}
              </div>
              <p className="settings-form-hint">
                Recevez des notifications et gérez vos releases directement depuis Telegram
              </p>
            </div>
          )}
        </div>

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={() => window.location.reload()}>
            Réinitialiser
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || charCount > maxBioLength || hasUrlErrors}
          >
            {saving ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin"></i>
                Sauvegarde...
              </>
            ) : (
              <>
                <i className="fa-solid fa-check"></i>
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </form>

      {/* Telegram Modal — masqué pour les Free sans config */}
      {(artist?.subscription_tier === 'premium' || artist?.telegram_id) && showTelegramModal && (
        <div
          className="telegram-modal-backdrop"
          role="presentation"
          onClick={() => onShowTelegramModal(false)}
          style={{ zIndex: 3000 }}
        >
          <div
            className="telegram-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="telegram-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="telegram-modal-header">
              <div>
                <p className="telegram-modal-kicker">Connexion Telegram</p>
                <h3 id="telegram-modal-title" className="telegram-modal-title">
                  Configurer l&apos;appairage du bot
                </h3>
              </div>
              <button
                className="btn-close-popup"
                onClick={() => onShowTelegramModal(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="telegram-modal-body">
              <div className="telegram-modal-steps">
                <div className="telegram-modal-step">
                  <span className="step-num">1</span>
                  <p>Ouvre <strong>@PassioArtistBot</strong> dans Telegram.</p>
                </div>
                <div className="telegram-modal-step">
                  <span className="step-num">2</span>
                  <p>Envoie la commande <code>/start</code> suivie du code affiché ci-dessous.</p>
                </div>
                <div className="telegram-modal-step">
                  <span className="step-num">3</span>
                  <p>Une fois le code validé, le bot te saluera par ton nom.</p>
                </div>
              </div>

              <div className="telegram-code-panel">
                <p className="telegram-code-label">Code temporaire</p>
                {telegramCode ? (
                  <>
                    <div className="telegram-code-box">{telegramCode}</div>
                    <p className="telegram-code-expiry">
                      Valable jusqu&apos;au {formatDateTime(telegramCodeExpiresAt)}
                    </p>
                    <div className="telegram-code-actions">
                      <button className="btn-copy-code" onClick={onCopyTelegramCode}>
                        {telegramCodeCopied ? 'Code copié' : 'Copier le code'}
                      </button>
                      <button
                        className="btn-telegram-generate btn-telegram-generate-secondary"
                        onClick={onGenerateTelegramCode}
                        disabled={telegramCodeLoading}
                      >
                        {telegramCodeLoading ? 'Génération...' : 'Régénérer'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="telegram-code-empty">
                      {telegramCodeLoading ? 'Génération du code en cours...' : 'Le code apparaîtra ici après génération.'}
                    </div>
                    <p className="telegram-code-expiry">Valable 10 minutes, usage unique.</p>
                  </>
                )}
              </div>

              {telegramCodeError && <p className="telegram-link-error">{telegramCodeError}</p>}

              <div className="telegram-modal-footer">
                <p className="telegram-link-note">
                  Le nouveau code remplace automatiquement l&apos;ancien tant qu&apos;il n&apos;a pas été consommé.
                </p>
                <a
                  href="https://t.me/PassioArtistBot"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-telegram-open"
                >
                  Ouvrir Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSection;
